<template>
  <div class="q-gutter-y-md keysearch-form">
    <div class="row">
      <div class="text-right text-subtitle1 text-weight-bold text-primary">簡易検索</div>
      <div class="key-search-rightarea">
        <div class="keysearch-formarea flex items-center">
          <q-input
            v-model="freeWordComputed"
            clearable
            placeholder=""
            outlined
            dense
            class="input-area left-input"
            @clear="clearFreeWord"
          />
        </div>
        <div class="key-search-form-caution q-mt-sm flex">
          <p class="q-mb-none">
            入力された検索ワードはATTO_ID、MDL No.、CAS
            No.、サプライヤ商品コード、化学名と総当たりで検索するため時間がかかります。<br />
          </p>
          <p class="q-mt-sm q-mb-none" style="margin-left: 1rem">
            検索条件がわかっている場合は詳細検索をご利用ください。
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="text-right text-subtitle1 text-weight-bold text-primary relative">詳細検索</div>
      <div class="key-search-rightarea">
        <div class="keysearch-formarea flex">
          <div class="key-search-label">
            <q-select
              v-model="keyDataComputed"
              clearable
              outlined
              :options="items"
              :display-value="keyDataComputed ? keyDataComputed : '選択してください'"
              dense
              @clear="clearKeySearch"
            />
            <q-checkbox
              v-model="completeMatchComputed"
              label="完全一致"
              color="primary"
              :disable="keyDataComputed !== '化学名'"
            ></q-checkbox>
          </div>
          <div class="keysearch-textarea flex">
            <q-input
              v-model="keyValueComputed"
              type="textarea"
              class="input-area"
              outlined
              autogrow
              dense
              :disable="keyDataComputed === ''"
            />
            <div v-if="keyDataComputed" class="key-search-key-value">
              <div class="key-search-key-value-note column">
                <span style="margin-bottom: 5px">入力例)</span>
                <span v-for="(item, index) in inputExample(keyDataComputed)" :key="index">{{
                  item
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <p class="key-search-form-caution q-mt-sm">
          複数件検索する場合は、改行して入力してください。最大{{
            $t('validation.search_max_count')
          }}件まで入力可能です。
        </p>
        <p class="key-search-form-caution q-mt-sm">
          詳細検索項目に化学名を選択した場合のみ、完全一致と部分一致の選択が可能です。化学名以外の詳細検索項目は完全一致のみとなります。
        </p>
        <p class="key-search-form-caution q-mt-sm">
          「試薬」と「その他のカテゴリー」は同時に検索できません。
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';

// 詳細検索キー
interface KeyPullDown {
  label: string;
  value: string;
}

interface Props {
  // フリーワード
  freeWord: string;
  // キー項目
  keyData: KeyPullDown;
  // 詳細値
  keyValue: string;
  // 完全一致
  completeMatch: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits([
  'clearFreeWord',
  'clearKeySearch',
  'changeFreeWord',
  'changeKeyData',
  'changeKeyValue',
  'changeCompleteMatch',
]);

// 詳細検索キー項目
const items = [
  {
    label: 'ATTO_ID',
    value: 'ATTO_ID',
  },
  {
    label: 'MDL No.',
    value: 'MDL_No',
  },
  {
    label: 'CAS No.',
    value: 'CAS_No',
  },
  {
    label: 'サプライヤ商品コード',
    value: 'サプライヤ商品コード',
  },
  {
    label: 'Smiles',
    value: 'SMILES',
  },
  {
    label: '化学名',
    value: 'NAME',
  },
];

// 入力例
const inputExample = (label: string) => {
  switch (label) {
    case 'ATTO_ID':
      return ['AT-000000001', 'AT-000000010', 'AT-000000025'];
    case 'MDL No.':
      return ['MFCD04967111', 'MFCD13186175', 'MFCD26959757'];
    case 'CAS No.':
      return ['50-32-8', '137-64-4', '91222-55-8'];
    case 'サプライヤ商品コード':
      return ['AM-1001', '10001-001e', '6127-2000-01'];
    case 'Smiles':
      return ['O=C1CCc2ccccc2C1', 'Nc1[nH]ncc1Br', 'Cl.Cl.CN(C1CNC1)C1COC1'];
    case '化学名':
      return ['Triphenylene', '(4-Methylpyridin-3-yl)methanol', '1-(2-nitrophenoxy)acetone'];
    default:
      return '';
  }
};

// フリーワード
const freeWordComputed = computed({
  get: () => props.freeWord,
  set: (value) => {
    if (value === null) {
      emit('changeFreeWord');
    } else {
      emit('changeFreeWord', value);
    }
  },
});

// 詳細検索プルダウン
const keyDataComputed = computed({
  get: () => props.keyData.label,
  set: (value) => {
    if (value === null) {
      emit('clearKeySearch');
    } else {
      emit('changeKeyData', value);
    }
  },
});

// 詳細検索テキスト
const keyValueComputed = computed({
  get: () => props.keyValue,
  set: (value) => {
    if (value === null) {
      emit('clearKeySearch');
    } else {
      emit('changeKeyValue', value);
    }
  },
});

// 完全一致
const completeMatchComputed = computed({
  get: () => props.completeMatch,
  set: (value) => {
    if (value === null) {
      emit('clearKeySearch');
    } else {
      emit('changeCompleteMatch', value);
    }
  },
});

// クリアイベント
const clearFreeWord = () => {
  emit('clearFreeWord');
};
const clearKeySearch = () => {
  emit('clearKeySearch');
};
</script>
<style scoped>
.keysearch-form {
  margin-bottom: 9px;
}

.keysearch-form .relative {
  position: relative;
}

.keysearch-form .key-search-rightarea {
  width: calc(100% - 140px);
}

.keysearch-form .keysearch-formarea .keysearch-textarea {
  width: calc(100% - 218px);
}

.keysearch-form .keysearch-formarea .keysearch-textarea .q-field {
  width: 510px;
  margin-right: 8px;
}

.keysearch-form .keysearch-formarea label.q-input {
  width: 728px;
}

.keysearch-form .keysearch-formarea .keysearch-textarea .key-search-key-value {
  width: 28%;
  width: calc(100% - 518px);
  position: relative;
}
.keysearch-form .key-search-key-value-note {
  position: absolute;
  width: 17em;
  top: 0;
  left: 0;
  font-size: 0.8rem;
  color: var(--q-plainBlack);
}

.keysearch-form .keysearch-formarea .key-search-label {
  width: 210px;
  margin-right: 8px;
}

.keysearch-form .text-subtitle1 {
  width: 80px;
  margin-right: 60px;
}

.keysearch-form .key-search-form-caution {
  font-size: 0.8rem;
  width: 100%;
  margin-bottom: 5px;
  color: var(--q-plainBlack);
  display: flex;
}

.keysearch-form .key-search-form-caution::before {
  content: '※';
  color: var(--q-plainBlack);
}
</style>
<style>
.keysearch-form .keysearch-textarea textarea {
  min-height: 76px !important;
}

.keysearch-form .key-search-rightarea .q-select .q-field__control {
  padding-right: 3px;
}

.keysearch-form .keysearch-formarea .q-field__control-container {
  font-size: 0.8rem;
}
</style>
